import { PageHeader } from '../../components/ui/PageHeader.tsx';
import { PageActions } from '../../components/ui/PageActions.tsx';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import { useMe } from '../../lib/auth.tsx';
import { STATUS_LABEL } from '../../lib/approvals.ts';
import { useGridQuery, useGridState } from '../../lib/grid.ts';
import { DataTable, type Column } from '../../components/DataTable.tsx';
import { Badge, LinkButton } from '../../components/ui/Button.tsx';
import { can } from '../../lib/perm.ts';
import { fmtDate, fmtDateTime } from '../../lib/date.ts';

const BOXES = [
  { key: 'inbox', label: '받은 결재' }, { key: 'sent', label: '올린 결재' }, { key: 'cc', label: '참조' }, { key: 'done', label: '처리한 결재' },
  { key: 'all', label: '전체', permission: 'approval.view_all' },
] as const;
type BoxKey = (typeof BOXES)[number]['key'];
const TONE: Record<string, 'neutral' | 'info' | 'success' | 'danger'> = { draft: 'neutral', in_progress: 'info', approved: 'success', rejected: 'danger', withdrawn: 'neutral' };
interface Row { id: string; title: string; status: string; roundNo: number; typeId: string; typeName: string; initiatorId: string; initiatorName: string; submittedAt: string | null; updatedAt: string; closedAt: string | null; pending: Array<{ name: string; assignee: string | null; waitingDays: number | null }> }

const columns: Column<Row>[] = [
  { key: 'title', label: '제목', filter: 'text', render: (r) => <><Link className="text-accent-strong hover:underline" to={`/approvals/${r.id}`}>{r.title}</Link>{r.roundNo > 1 && <span className="ml-1 text-[11px] text-slate-500">R{r.roundNo}</span>}</> },
  { key: 'typeId', label: '유형', width: '10rem', filter: 'select', sortable: false, render: (r) => r.typeName },
  { key: 'initiatorName', label: '기안자', width: '7rem', filter: 'text', sortable: false },
  { key: 'status', label: '상태', width: '6rem', filter: 'select', render: (r) => <Badge tone={TONE[r.status] ?? 'neutral'}>{STATUS_LABEL[r.status] ?? r.status}</Badge> },
  { key: 'pending', label: '현재 단계 · 대기', row: 2, span: 6, sortable: false, render: (r) => <span className="text-[12px]">{r.pending.map((p, i) => <div key={i}>{p.name} · {p.assignee}{p.waitingDays != null && p.waitingDays > 0 && <span className="ml-1 text-sky-700">{p.waitingDays}일 대기</span>}</div>)}</span> },
  { key: 'submittedAt', label: '제출', width: '6.5rem', filter: 'date', render: (r) => <span className="whitespace-nowrap text-[12px]">{r.submittedAt ? fmtDate(r.submittedAt) : '—'}</span> },
  { key: 'updatedAt', label: '최근 변경', width: '10rem', filter: 'date', render: (r) => <span className="whitespace-nowrap text-[12px]">{fmtDateTime(r.updatedAt)}</span> },
];

/** 결재함 — 서버사이드 그리드 */
export function ApprovalsInboxPage() {
  const me = useMe();
  const { state, set } = useGridState('ap', { sort: { col: 'updatedAt', dir: 'desc' } });
  const box = (state.filters.find((f) => f.col === '_box')?.value[0] ?? 'inbox') as BoxKey;
  const setBox = (b: BoxKey) => set({ filters: [{ col: '_box', op: 'eq', value: [b] }] });
  const serverState = { ...state, filters: state.filters.filter((f) => f.col !== '_box') };
  const stats = useQuery({ queryKey: ['approval-stats'], queryFn: () => api<{ inbox: number; sentOpen: number }>('/approvals/stats') });
  const rows = useGridQuery<Row>('approvals-grid', `/approvals/grid/${box}`, serverState);
  return (
    <div>
      <PageHeader title="결재함" />
      <div className="flex gap-1 mb-3 border-b border-slate-200">
        {BOXES.filter((b) => !('permission' in b) || can(me.data, b.permission as string)).map((b) => (
          <button key={b.key} onClick={() => setBox(b.key)} className={`px-2.5 py-1.5 text-xs -mb-px border-b-2 ${box === b.key ? 'border-accent text-accent-strong font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'}`}>
            {b.label}{b.key === 'inbox' && stats.data?.inbox ? <span className="ml-1 bg-accent text-white text-[11px] px-1.5">{stats.data.inbox}</span> : null}
          </button>
        ))}
      </div>
      {rows.isError && <div className="text-sm text-red-600 mb-2">{(rows.error as Error).message}</div>}
      <DataTable id="approvals-inbox" columns={columns} data={rows.data} state={serverState} onChange={(p) => set({ ...p, filters: [{ col: '_box', op: 'eq', value: [box] }, ...(p.filters ?? serverState.filters)] })} isFetching={rows.isFetching} />
      <PageActions><LinkButton to="/approvals/new">기안하기</LinkButton></PageActions>
    </div>
  );
}
